/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useClient } from "../ClientContext";
import { LoadingPage, ErrorPage } from "../FullScreenView";
import { BackendError } from "../utils/backend";
import { recordingTarget } from "./recordingTarget";
import { widget } from "../widget";

/** Reuse the paired Matrix session without publishing a private recording link. */
export function StaffRecordingRedirect() {
  const { client } = useClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<Error>();
  useEffect(() => {
    const abort = new AbortController();
    void recordingTarget(client, location.search, abort.signal)
      .then((target) => {
        if (!abort.signal.aborted) window.location.assign(target.href);
      })
      .catch((cause) => {
        if (abort.signal.aborted) return;
        if (cause instanceof BackendError && cause.status === 428) {
          void navigate("/login", { state: { from: location }, replace: true });
          return;
        }
        setError(
          cause instanceof Error
            ? cause
            : new Error(t("instance.errors.request_failed")),
        );
      });
    return () => abort.abort();
  }, [client, location, navigate, t]);
  return error ? <ErrorPage widget={widget} error={error} /> : <LoadingPage />;
}
